import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { ApiError, apiFetch } from '../api/client.ts';
import { Button } from '../components/Button.tsx';
import { Card } from '../components/Card.tsx';
import { FormField } from '../components/FormField.tsx';
import { useDocumentTitle } from '../components/useDocumentTitle.ts';

export function ForgotPasswordPage(): React.ReactElement {
  useDocumentTitle('Forgot password');
  const [searchParams] = useSearchParams();
  const [audience, setAudience] = useState<'agent' | 'contact'>(
    searchParams.get('audience') === 'client' ? 'contact' : 'agent',
  );
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const path = audience === 'agent' ? '/api/auth/agent/reset/start' : '/api/portal/auth/reset/start';
      await apiFetch(path, { method: 'POST', json: { email } });
      setSent(true);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not request reset.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto max-w-md space-y-4">
      <Card>
        <div className="mb-4 inline-flex w-full overflow-hidden rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white">
          <button
            type="button"
            onClick={() => setAudience('agent')}
            className={
              'flex-1 px-4 py-2 text-sm font-medium transition ' +
              (audience === 'agent'
                ? 'bg-[var(--color-t3-blue)] text-white'
                : 'text-[var(--color-body)] hover:bg-[var(--color-surface)]')
            }
          >
            Technician
          </button>
          <button
            type="button"
            onClick={() => setAudience('contact')}
            className={
              'flex-1 px-4 py-2 text-sm font-medium transition ' +
              (audience === 'contact'
                ? 'bg-[var(--color-t3-blue)] text-white'
                : 'text-[var(--color-body)] hover:bg-[var(--color-surface)]')
            }
          >
            Client
          </button>
        </div>
        <h1 className="text-xl font-bold text-[var(--color-header)]">Forgot your password?</h1>
        <p className="mt-1 text-sm text-[var(--color-muted)]">
          Enter your email and we'll send a single-use link valid for 1 hour. Your TOTP code stays
          the same.
        </p>
        {sent ? (
          <p className="mt-4 rounded-[var(--radius-button)] bg-green-50 p-3 text-sm text-green-900">
            If an account exists for that email, a reset link is on the way. (We don't disclose
            whether the email matched.)
          </p>
        ) : (
          <form onSubmit={submit} className="mt-4 space-y-3">
            <FormField
              label="Email"
              name="email"
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            {error && <p className="text-sm text-[var(--color-danger)]">{error}</p>}
            <Button type="submit" disabled={busy || !email} className="w-full">
              {busy ? 'Sending…' : 'Send reset link'}
            </Button>
          </form>
        )}
        <p className="mt-3 text-center text-sm">
          <Link to="/login" className="text-[var(--color-muted)] hover:underline">
            ← Back to sign in
          </Link>
        </p>
      </Card>
    </div>
  );
}
